import BookingService from './bookings/BookingService';
import TrainingHistoryService from './history/TrainingHistoryService';
import PersonalRecordService from './personal-records/PersonalRecordService';

// n8n workflow that receives the payload built below and returns the
// AI generated weekly summary/insights for the athlete.
const N8N_AI_COACH_WEBHOOK_URL = 
  'https://codersden.app.n8n.cloud/webhook/ce8ff7c5-4a2f-49ee-9c14-f92985faf995';

// WOD dates/times sometimes come back from the backend as arrays
// (e.g. date: [year, month, day], startTime: [hour, minute, second]).
// This helper normalizes both that array format and regular date strings
// into a real JS Date so we can safely compare/filter by period.
const toDate = (dateValue, timeValue) => {
  let year, month, day;
  if (Array.isArray(dateValue)) {
    [year, month, day] = dateValue;
  } else {
    const parsed = new Date(dateValue);
    year = parsed.getFullYear();
    month = parsed.getMonth() + 1;
    day = parsed.getDate();
  }

  let hour = 0;
  let minute = 0;
  let second = 0;
  if (Array.isArray(timeValue)) {
    [hour, minute, second = 0] = timeValue;
  }

  return new Date(year, month - 1, day, hour, minute, second);
};

const toISODate = date => date.toISOString().split('T')[0];

const isWithinPeriod = (date, from, to) => date >= from && date <= to;

const AICoachService = {
  /**
   * Builds the payload sent to the n8n "AI Coach" workflow, using the
   * different WOD services already available in the app:
   *  - BookingService: attendance (booked/cancelled/no-shows) for the period
   *  - TrainingHistoryService: completed classes and previous 4 week average
   *  - PersonalRecordService: PRs achieved during the period
   */
  async buildPayload() {
    const profile = JSON.parse(localStorage.getItem('profile'));

    // Current period: last 7 days, including today.
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const periodStart = new Date(today);
    periodStart.setDate(today.getDate() - 6);

    // The 4 weeks immediately before the current period, used to calculate
    // the athlete's previous average weekly attendance.
    const previousPeriodEnd = new Date(periodStart);
    previousPeriodEnd.setDate(previousPeriodEnd.getDate() - 1);
    const previousPeriodStart = new Date(previousPeriodEnd);
    previousPeriodStart.setDate(previousPeriodStart.getDate() - 27);

    const [bookings, trainingHistory, personalRecords] = await Promise.all([
      BookingService.retrieveAllBookingsByUser(),
      TrainingHistoryService.retrieveAllTrainingHistoryByProfile(
        profile.identifier
      ),
      PersonalRecordService.retrieveAllPersonalRecordsByUser()
    ]);

    // --- Attendance for the current period ---
    const bookingsInPeriod = (bookings || []).filter(b =>
      isWithinPeriod(toDate(b.date, b.startTime), periodStart, today)
    );

    const isCancelled = b => b.status === 'cancelled' || b.cancelled === true;
    const isNoShow = b => b.status === 'no-show' || b.noShow === true;
    const isWaitlisted = b =>
      Array.isArray(b.waitlisted) &&
      b.waitlisted.some(w => w.identifier === profile.identifier);

    const attendance = {
      booked: bookingsInPeriod.length,
      attended: bookingsInPeriod.filter(
        b => !isCancelled(b) && !isNoShow(b) && !isWaitlisted(b)
      ).length,
      cancelled: bookingsInPeriod.filter(isCancelled).length,
      noShows: bookingsInPeriod.filter(isNoShow).length
    };

    // --- Completed classes for the current period ---
    const completedClasses = (trainingHistory || [])
      .filter(h => isWithinPeriod(toDate(h.date, h.startTime), periodStart, today))
      .map(h => ({
        date: toISODate(toDate(h.date)),
        className: h.name,
        focus: h.focus || [],
        results: h.results || {}
      }));

    // --- Previous 4 week average attendance ---
    const classesInPreviousFourWeeks = (trainingHistory || []).filter(h =>
      isWithinPeriod(
        toDate(h.date, h.startTime),
        previousPeriodStart,
        previousPeriodEnd
      )
    );
    const previousFourWeekAverage = {
      classesAttended: Number((classesInPreviousFourWeeks.length / 4).toFixed(1))
    };

    // --- Personal records achieved within the current period ---
    const personalRecordsInPeriod = (personalRecords || [])
      .filter(pr => pr.date && isWithinPeriod(new Date(pr.date), periodStart, today))
      .map(pr => ({
        movement: pr.type,
        previousValue: pr.previousValue ?? null,
        currentValue: pr.value,
        unit: pr.unit
      }));

    return {
      profile: {
        identifier: profile.identifier,
        firstName: profile.firstName,
        lastName: profile.lastName,
        email: profile.email
      },
      period: {
        from: toISODate(periodStart),
        to: toISODate(today)
      },
      attendance,
      previousFourWeekAverage,
      completedClasses,
      personalRecords: personalRecordsInPeriod
    };
  },

  async retrieveSummaryByProfile() {
    try {
      const payload = await this.buildPayload();
      console.log('AI Coach payload:', payload);

      const response = await fetch(N8N_AI_COACH_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(
          `Failed to retrieve AI Coach summary: ${response.status}`
        );
      }

      const results = await response.json();
      const summary = results.week;
      console.log('AI Coach summary:', summary);
      return summary;
    } catch (error) {
      console.error('Error fetching AICoach', error);
      throw error;
    }
  }
};
export default AICoachService;
